import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { authedFetch } from '../lib/api';

interface WaTemplate {
  name: string;
  language: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED';
  category: string;
}

type MessengerTag = 'CONFIRMED_EVENT_UPDATE' | 'POST_PURCHASE_UPDATE' | 'ACCOUNT_UPDATE' | 'HUMAN_AGENT';

interface Props {
  tenantCode: string;
  inboxId: string;
  channel: 'whatsapp' | 'facebook';
  onSelect: (sel:
    | { kind: 'template'; template: { name: string; language: string } }
    | { kind: 'tag'; tag: MessengerTag }
    | null
  ) => void;
  selected:
    | { kind: 'template'; template: { name: string; language: string } }
    | { kind: 'tag'; tag: MessengerTag }
    | null;
}

const FB_TAGS: { tag: MessengerTag; label: string }[] = [
  { tag: 'CONFIRMED_EVENT_UPDATE', label: 'Confirmed event update' },
  { tag: 'POST_PURCHASE_UPDATE', label: 'Post-purchase update' },
  { tag: 'ACCOUNT_UPDATE', label: 'Account update' },
  { tag: 'HUMAN_AGENT', label: 'Human agent (7-day, opt-in required)' },
];

// Surfaces the only outbound options Meta permits outside the 24h window.
// WA: approved templates pulled from /agent/wa-templates (filtered to APPROVED).
// FB: a fixed allowlist of message tags.
export function OutsideWindowPicker({ tenantCode, inboxId, channel, onSelect, selected }: Props) {
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (channel !== 'whatsapp') return;
    setLoading(true);
    setErr(null);
    authedFetch<{ items: WaTemplate[] }>(`/agent/wa-templates?tenantCode=${encodeURIComponent(tenantCode)}&inboxId=${encodeURIComponent(inboxId)}`)
      .then((r: { items: WaTemplate[] }) => setTemplates(r.items.filter((t: WaTemplate) => t.status === 'APPROVED')))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [tenantCode, inboxId, channel]);

  if (channel === 'whatsapp') {
    return (
      <div className="mx-2 mb-1 rounded-md border border-gray-300 bg-gray-50 text-xs">
        <div className="px-3 py-1.5 border-b border-gray-200 font-medium text-gray-700">Approved templates</div>
        {loading && <div className="px-3 py-2 text-gray-500">Loading…</div>}
        {err && <div className="px-3 py-2 text-red-600">{err}</div>}
        {!loading && !err && templates.length === 0 && (
          <div className="px-3 py-2 text-gray-500">No approved templates on this inbox.</div>
        )}
        <ul className="max-h-40 overflow-y-auto">
          {templates.map((t) => {
            const isSel = selected?.kind === 'template' && selected.template.name === t.name && selected.template.language === t.language;
            return (
              <li key={`${t.name}/${t.language}`}>
                <button
                  type="button"
                  onClick={() => onSelect(isSel ? null : { kind: 'template', template: { name: t.name, language: t.language } })}
                  className={`w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center justify-between ${isSel ? 'bg-blue-50' : ''}`}
                >
                  <span className="truncate">
                    <span className="font-mono text-gray-700">{t.name}</span>
                    <span className="text-gray-500 ml-2">{t.language} · {t.category}</span>
                  </span>
                  {isSel && <Check className="w-3 h-3 text-blue-600" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  // Facebook
  return (
    <div className="mx-2 mb-1 rounded-md border border-gray-300 bg-gray-50 text-xs">
      <div className="px-3 py-1.5 border-b border-gray-200 font-medium text-gray-700">Messenger tag</div>
      <ul>
        {FB_TAGS.map((t) => {
          const isSel = selected?.kind === 'tag' && selected.tag === t.tag;
          return (
            <li key={t.tag}>
              <button
                type="button"
                onClick={() => onSelect(isSel ? null : { kind: 'tag', tag: t.tag })}
                className={`w-full text-left px-3 py-1.5 hover:bg-gray-100 flex items-center justify-between ${isSel ? 'bg-blue-50' : ''}`}
              >
                <span>
                  <span className="font-mono text-gray-700">{t.tag}</span>
                  <span className="text-gray-500 ml-2">{t.label}</span>
                </span>
                {isSel && <Check className="w-3 h-3 text-blue-600" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
